import { logException } from "@wss/error-tracking";
import { getErrorMessage } from "@wss/error-tracking/utils";
import delegate from "es6-delegate";
import autobind from "autobind-decorator";
import Cart from "../Cart";
import { ValidationError } from "../lazyValidate";
import AutoReorder from "./AutoReorder";
import addToCartSummaryHtml from "./addToCartSummary.html";
import SpecialQuantity from "./SpecialQuantity";
import CustomQuantity from "./CustomQuantity";
import Product from "./Product";
import Pricing from "./Pricing";
import RibbonList from "./RibbonList";
import alreadyAddedToYourCartHtml from "./alreadyAddedToYourCart.html";
import Unavailable from "./Unavailable";
import CarouselSuffix from "./CarouselSuffix";
import PlatinumPlusExclusiveSuffix from "./PlatinumPlusExclusiveSuffix";

export default class AddToCart {
  totalAddedToCart = 0;

  lastAddToCart = 0;

  autoReorder = {};

  get successContainer() {
    return this.container.querySelector(".success-container") || {};
  }

  get errorMessageContainer() {
    return this.form.querySelector("[data-component=errorMessageContainer]");
  }

  set errorMessage(message) {
    if (message) {
      this.errorMessageContainer.hidden = false;
      this.errorMessageContainer.textContent = message;
    } else {
      this.currentErrorSource = null;
      this.errorMessageContainer.hidden = true;
    }
  }

  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    if (!container) return;
    this.container = container;
    new Unavailable(this.container);

    this.form = this.container.querySelector(
      ".add-to-cart form:not([data-target=notifyMe]):not(.exclude-legacy-addtocart)",
    );
    if (!this.form) return;

    delegate(
      this.form,
      "click",
      "[data-action=addToCart]",
      this.addToCartHandler,
    );
    container.addEventListener("autoReorderChange", this.autoReorderChange);
    container.addEventListener(
      "Product.quantity.change",
      this.validateWithoutErrorMessage,
    );
    container.addEventListener(
      "ItemNumber.change",
      this.validateWithoutErrorMessage,
    );

    this.product = new Product(this.form);

    if (this.container.querySelector("[data-component=AutoReorder]")) {
      this.autoReorder = new AutoReorder(this.container, this.product);
    }

    this.cart = new Cart();
    this.specialQuantity = new SpecialQuantity(
      this.container.querySelector("[data-component=specialQuantity]"),
    );
    new CustomQuantity(
      this.container.querySelector("[data-component=customQuantity]"),
    );
    new Pricing(this.container.querySelector("[data-component=Pricing]"));
    new RibbonList(this.container.querySelector("[data-component=RibbonList]"));
    new CarouselSuffix(
      this.container.querySelector("[data-component=Suffixes]"),
    );
    new PlatinumPlusExclusiveSuffix(container);

    this.dynamicModules();
  }

  async dynamicModules() {
    const getPriceByEmail = this.container.querySelector(
      "[data-component=getPriceByEmail]",
    );

    if (getPriceByEmail) {
      const { default: GetPriceByEmail } = await import("./GetPriceByEmail");
      new GetPriceByEmail(getPriceByEmail);
    }

    const getPriceByTextForms = this.container.querySelectorAll(
      "[data-component=getPriceByText]",
    );

    if (getPriceByTextForms && getPriceByTextForms.length) {
      const { default: GetPriceByText } = await import("./GetPriceByText");
      getPriceByTextForms.forEach((getPriceByTextForm) => {
        new GetPriceByText(getPriceByTextForm);
      });
    }
  }

  @autobind
  async validateWithoutErrorMessage() {
    await this.product.valid({ skip: "accessoriesValid" }).catch(this.error);
    this.errorMessage = "";
  }

  @autobind
  async autoReorderChange() {
    try {
      if (this.autoReorder.enabled) await this.autoReorder.valid();
      if (this.currentErrorSource === AutoReorder) this.errorMessage = "";
    } catch (error) {
      await logException(
        new Error(`AutoReorder validation failed: ${getErrorMessage}`),
      );
    }
  }

  successMessage() {
    this.successContainer.innerHTML = addToCartSummaryHtml(
      this.totalAddedToCart,
    );
  }

  @autobind
  addToCartHandler(event) {
    if (event.target.dataset.outOfStock) return Promise.reject();
    event.preventDefault();
    return this.addToCart().catch(this.error);
  }

  async addToCart() {
    await this.product.valid();
    if (this.product.outletItem) this.renderAlreadyAddedToYourCartHtml();

    if (this.autoReorder.enabled) await this.autoReorder.valid();

    if (this.autoReorder.existing) {
      this.errorMessage = "";
      await this.autoReorder.addItemToExistingAutoReorder();
      return;
    }

    const body = new FormData(this.product.form);
    body.set("auto_reorder_interval_id", "0");
    if (this.product.accessories)
      body.set("accessories", this.product.accessories);

    if (this.autoReorder.new) {
      body.set(
        "auto_reorder_interval_id",
        this.autoReorder.intervalDropdown.value,
      );
    }

    const addToCartBtn = this.container.querySelector(
      "[data-action=addToCart]",
    );
    if (addToCartBtn && addToCartBtn.dataset.interrupt === "true") {
      return;
    }
    this.success();
    const cartResponse = await this.cart.add(body);
    if (
      cartResponse?.redirected &&
      String(cartResponse?.url).search("productaccessories") !== -1
    ) {
      this.errorMessage =
        "This item is missing required accessories, please select the required accessories.";
      this.rollback();
    }
  }

  renderAlreadyAddedToYourCartHtml() {
    this.specialQuantity.container.innerHTML = alreadyAddedToYourCartHtml;
  }

  rollback() {
    this.cart.count -= this.lastAddToCart;
    this.totalAddedToCart -= this.lastAddToCart;
    this.lastAddToCart = 0;
    this.successContainer.innerHTML = "";
  }

  success() {
    this.errorMessage = "";
    this.lastAddToCart = this.product.quantity;
    this.totalAddedToCart += this.product.quantity;
    this.successMessage();
    this.container.dispatchEvent(
      new Event("AddToCart.success", { bubbles: true }),
    );
  }

  @autobind
  error(error) {
    let { message } = error;

    if (error.constructor !== ValidationError) {
      import("@sentry/react").then((args) => {
        const { captureException } = args.default ?? args;
        captureException(error);
      });

      message = "There was an error adding this item to your cart.";
      this.rollback();
    }

    this.currentErrorSource = error.errorSource;
    this.errorMessage = message;
  }
}
